// frontend/src/components/integrations/SuggestLinksModal.js
import React, { useState } from 'react';
import { Modal, Button, Progress, Space, Typography, Divider, Empty, Spin, Tag, Tooltip } from 'antd';

const { Text } = Typography;

const INTEGRATION_COLORS = {
  hubspot: '#e8440a',
  esputnik: '#0070cc',
  stripe: '#5433c5',
};

function ConfidenceBar({ score }) {
  const pct = Math.round(score * 100);
  const color = pct >= 80 ? '#28a745' : pct >= 60 ? '#ffc107' : '#dc3545';
  return (
    <div style={{ minWidth: 100 }}>
      <Text style={{ fontSize: 11, color: 'var(--text-muted)' }}>{pct}% match</Text>
      <Progress
        percent={pct}
        size="small"
        showInfo={false}
        strokeColor={color}
        style={{ margin: 0 }}
      />
    </div>
  );
}

export default function SuggestLinksModal({ open, suggestions, loading, onApply, onCancel }) {
  const [accepted, setAccepted] = useState({});
  const [rejected, setRejected] = useState({});

  const makeKey = s => `${s.canonical_key}__${s.integration_id}__${s.ext_key}`;

  const accept = (s) => {
    const k = makeKey(s);
    setAccepted(prev => ({ ...prev, [k]: s }));
    setRejected(prev => { const n = { ...prev }; delete n[k]; return n; });
  };

  const reject = (s) => {
    const k = makeKey(s);
    setRejected(prev => ({ ...prev, [k]: true }));
    setAccepted(prev => { const n = { ...prev }; delete n[k]; return n; });
  };

  const highConf = suggestions.filter(s => s.score >= 0.75);
  const medConf  = suggestions.filter(s => s.score >= 0.5 && s.score < 0.75);

  const acceptedList = Object.values(accepted);
  const acceptedCount = acceptedList.length;
  const rejectedCount = Object.keys(rejected).length;
  const pendingCount  = suggestions.length - acceptedCount - rejectedCount;

  const handleApply = () => {
    onApply(acceptedList);
    setAccepted({});
    setRejected({});
  };

  function SuggestionRow({ s }) {
    const k = makeKey(s);
    const isAccepted = !!accepted[k];
    const isRejected = !!rejected[k];
    const dotColor = INTEGRATION_COLORS[s.type_id] || '#888';
    const isAiSourced = s.source === 'llm' || s.source === 'merged';

    return (
      <div style={{
        border: `1px solid ${isAccepted ? '#b7ebc0' : isRejected ? '#ffd6d6' : 'var(--border-color)'}`,
        borderRadius: 8,
        marginBottom: 6,
        padding: '10px 12px',
        background: isAccepted ? '#f0fff4' : isRejected ? '#fff5f5' : 'var(--bg-secondary)',
        opacity: isRejected ? 0.6 : 1,
        transition: 'all 0.15s',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 20px 1fr 120px 130px',
          alignItems: 'center',
          gap: 10,
        }}>
          <div>
            <Space size={4}>
              <Text strong style={{ fontSize: 12 }}>{s.canonical_key}</Text>
              {isAiSourced && (
                <Tooltip title={s.source === 'merged' ? 'Confirmed by heuristic + AI' : 'Found by AI'}>
                  <Tag color="purple" style={{ fontSize: 10, lineHeight: '14px', padding: '0 4px' }}>AI</Tag>
                </Tooltip>
              )}
            </Space>
          </div>
          <Text style={{ color: 'var(--text-muted)', textAlign: 'center' }}>→</Text>
          <div>
            <Space size={4}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, display: 'inline-block' }} />
              <Text style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.integration_name || s.type_id}</Text>
            </Space>
            <br />
            <Text style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{s.ext_key}</Text>
          </div>
          <ConfidenceBar score={s.score} />
          <Space size={6} style={{ justifyContent: 'flex-end' }}>
            <Button
              size="small"
              type={isAccepted ? 'primary' : 'default'}
              onClick={() => accept(s)}
            >
              {isAccepted ? '✓ Accepted' : '✓ Accept'}
            </Button>
            {!isAccepted && (
              <Button
                size="small"
                danger={isRejected}
                onClick={() => reject(s)}
              >✗</Button>
            )}
          </Space>
        </div>
        {s.rationale && (
          <Text style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', display: 'block', marginTop: 6 }}>
            {s.rationale}
          </Text>
        )}
      </div>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title="Suggested Property Links"
      footer={[
        <span key="counter" style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 'auto' }}>
          {acceptedCount} accepted · {rejectedCount} rejected · {pendingCount} pending
        </span>,
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button
          key="apply"
          type="primary"
          disabled={acceptedCount === 0}
          onClick={handleApply}
        >
          Apply {acceptedCount} accepted link{acceptedCount !== 1 ? 's' : ''}
        </Button>,
      ]}
      width="80vw"
      style={{ top: 80, minWidth: 700 }}
    >
      {loading && <Spin tip="Analysing integrations…" style={{ display: 'block', margin: '24px auto' }} />}
      {!loading && suggestions.length === 0 && (
        <Empty description="No suggestions found. Try adding more contacts first." />
      )}
      {!loading && highConf.length > 0 && (
        <>
          <Text style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            High confidence
          </Text>
          <div style={{ marginTop: 8 }}>
            {highConf.map(s => <SuggestionRow key={makeKey(s)} s={s} />)}
          </div>
        </>
      )}
      {!loading && medConf.length > 0 && (
        <>
          <Divider style={{ margin: '12px 0 8px' }} />
          <Text style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Medium confidence
          </Text>
          <div style={{ marginTop: 8 }}>
            {medConf.map(s => <SuggestionRow key={makeKey(s)} s={s} />)}
          </div>
        </>
      )}
    </Modal>
  );
}
